import React, { useEffect, useState } from "react";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { fetchData } from "@/lib/helper";
import ProcessingScreen from "./processing";
import CongratsScreen from "./Congrats";
import SorryScreen from "./Sorry";
import PartnerSignUp from "./PartnerSignUp";
import PartnerDynamicScreen from "@/b2b/pages/PartnerDynamicScreen";
import RedirectScreenHelper from "./RedirectScreenHelper";
import BankingSorry from "./BankingSorryScreen";
import BankingLimitExceededScreen from "./BankingLimitExceededScreen";
import TransitionScreen from "./TransitionScreen";
import NotFoundPage from "./NotFoundPage";
import { useTheme } from "@/b2b/context/ThemeContext";
import { withoutLeadPartners } from "@/b2b/config/partnerJourney";
import { getPartnerConfig } from "@/lib/apis";
import PartnerLogin from "./PartnerLoginScreen";

export default function PartnerScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userIdentifier } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  // A partner is valid if it's a hardcoded journey partner OR exists in the
  // backend (any partner created from the B2B dashboard). This removes the need
  // to hardcode every new partner in `withoutLeadPartners`.
  const inHardcoded = withoutLeadPartners.includes(userIdentifier);
  const [dbKnown, setDbKnown] = useState(inHardcoded ? true : null); // null = checking
  const isKnownPartner = inHardcoded || dbKnown === true;

  /** ✅ Get `step` Directly from the URL */
  // `rawStep` is null when the URL carries no step at all — a bare `/p/<partner>` landing. `step`
  // keeps the old "loading" default for rendering; the two are NOT interchangeable, because
  // "no step" and "step=loading" mean different things to the effect below.
  const rawStep = searchParams.get("step");
  const step = rawStep || "loading";

  /** ✅ Fetch User Data */
  const getUserData = async () => {
    try {
      setLoading(true);
      const data = await fetchData("user/me");
      setUserData(data);
      setFetchError(false); // Reset fetch error if successful
    } catch (error) {
      console.error("Failed to fetch user data:", error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  /** ✅ Store `userIdentifier` in localStorage */
  useEffect(() => {
    if (userIdentifier) {
      localStorage.setItem("user_identifier", userIdentifier);
    }
  }, [userIdentifier]);

  /** ✅ Resolve non-hardcoded tags against the backend (DB-driven partners) */
  useEffect(() => {
    if (!userIdentifier || inHardcoded) return;
    let active = true;
    getPartnerConfig(userIdentifier).then((cfg) => {
      if (active) setDbKnown(!!cfg?.exists);
    });
    return () => {
      active = false;
    };
  }, [userIdentifier, inHardcoded]);

  useEffect(() => {
    if (userIdentifier && isKnownPartner) {
      localStorage.setItem("partnerIdentifier", userIdentifier);

      // Only ask "who is this?" when the URL says we are mid-flow.
      //
      // `/p/<partner>` is a PUBLIC landing page — a partner's marketing link, typed or tapped by
      // someone who has not identified themselves yet. Asking `user/me` there sends whatever
      // SIGNUP_TOKEN_CP is lying around in localStorage, so on any device where someone previously
      // finished a journey the visitor was routed by the PREVIOUS tenant's result: straight to
      // "You are eligible for ZERO DEPOSIT". Reproduced in a browser — clean storage lands on
      // sign-up, a leftover token lands on congrats — which is exactly why it looked intermittent:
      // it stops once that token finally expires.
      //
      // The two legitimate reasons to land here without a step are `PartnerSignUp` and
      // `PartnerLogin` right after a successful OTP, and both now say `?step=loading` so that
      // intent is in the URL instead of being guessed. A bare landing falls through to the
      // navigation effect below, which sends an unidentified visitor to sign-up — the behaviour a
      // clean browser already had.
      if (rawStep && ["sorry", "congrats", "loading"].includes(rawStep)) {
        getUserData();
      }
    }
  }, [userIdentifier, rawStep, isKnownPartner]);

  /** ✅ Prevent `searchParams` From Overwriting `step` */
  useEffect(() => {
    if (searchParams.get("step") !== step) {
      setSearchParams({ step });
    }
  }, [step, setSearchParams]);

  if (!theme) return null;

  /** ✅ Handle navigation after fetching userData */
  useEffect(() => {
    if (!loading && isKnownPartner) {
      if (!userData) {
        // ✅ If fetch fails, navigate to sign-up
        navigate(`/p/${userIdentifier}?step=sign-up`);
      } else if (userData) {
        console.log("Inside the condition: ", userData.isEligible);
        if (userData.isEligible) {
          navigate(`/p/${userIdentifier}?step=congrats`);
        } else if (userData.isEligible === false) {
          navigate(`/p/${userIdentifier}?step=sorry`);
        } else {
          navigate(`/p/${userIdentifier}?step=eligibility-check`);
        }
      }
    }
  }, [loading, userIdentifier, userData, fetchError, isKnownPartner]);

  // Confirmed unknown partner: not hardcoded AND the backend says it doesn't
  // exist. Previously this hung forever on the "Processing" screen.
  if (!inHardcoded && dbKnown === false) return <NotFoundPage />;

  /** ✅ Render Component Based on `step` */
  switch (step) {
    case "congrats":
      return <CongratsScreen theme={theme} allowButton={false} />;
    case "sorry":
      return <SorryScreen theme={theme} />;
    case "eligibility-check":
      return <PartnerDynamicScreen step={step} />;
    case "loading":
      return <ProcessingScreen theme={theme} recoverable />; // Fallback UI
    case "redirect":
      return <RedirectScreenHelper theme={theme} />;
    case "processing":
      return <ProcessingScreen theme={theme} recoverable />;
    case "banking-sorry":
      return <BankingSorry theme={theme} />;
    case "banking-limit":
      return <BankingLimitExceededScreen theme={theme} />;
    case "trn":
      return <TransitionScreen />;
    case "login":
      return <PartnerLogin theme={theme} />;
    case "sign-up":
      return <PartnerSignUp theme={theme} />;
    default:
      return <NotFoundPage />;
  }
}
